/* eslint-disable camelcase */
import React, { useState } from 'react';
import { TClientData, TPageNotification, TUser } from '../../../types';
import { executeHttpRequest } from '../../../epics/api';
import { getErrorMessages } from '../../../utils';

type TFields = {
    default_nature_of_business: string;
    default_required_qualifications: string;
    default_safety_risks: string;
    default_available_facilities: string;
    default_expenses_payable: string;
}

type TClientAssignmentDetailsProps = {
    clientId: string | number;
    clientData: TClientData;
    notification: TPageNotification;
    setClientData: (clientData: TClientData) => void;
    user: TUser;
};

export default function ClientAssignmentDetails({
    clientId,
    clientData,
    notification,
    setClientData,
    user,
}: TClientAssignmentDetailsProps) {
    const [error, setError] = useState<string | null>(null);
    const [fields, setFields] = useState<TFields | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleTextFieldChange = (e: any) => {
        setFields({
            ...fields,
            [e.target.name]: e.target.value,
        });
    };

    const handleSaveButtonClick = () => {
        setIsLoading(true);
        setError(null);

        const payload = new FormData();
        Object.entries(fields)
            .filter(([key, value]: [keyof TFields, string]) => value !== clientData[key])
            .forEach(([key, value]) => payload.append(key, value));

        executeHttpRequest(`clients/${clientId}/`, 'patch', payload)
            .then(response => {
                setIsLoading(false);
                setIsEditing(false);
                setClientData(response.data.content);
                notification.success('Success', 'Default assignment details updated!');
            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    const handleCancelButtonClick = () => {
        setError(null);
        setFields(null);
        setIsEditing(false);
    };

    const handleEditButtonClick = () => {
        setFields({
            default_nature_of_business: clientData.default_nature_of_business,
            default_required_qualifications: clientData.default_required_qualifications,
            default_safety_risks: clientData.default_safety_risks,
            default_available_facilities: clientData.default_available_facilities,
            default_expenses_payable: clientData.default_expenses_payable,
        });
        setIsEditing(true);
    };

    const renderSectionButtons = () => (
        isEditing ? (
            <div className="freelancer-details-section__btns">
                <button
                    type="button"
                    disabled={isLoading}
                    className="freelancer-details-section__btn"
                    onClick={handleSaveButtonClick}
                >
                    Save
                </button>
                <button
                    data-testid="cancelBtn"
                    type="button"
                    disabled={isLoading}
                    className="freelancer-details-section__btn"
                    onClick={handleCancelButtonClick}
                >
                    Cancel
                </button>
            </div>
        ) : (
            <div className="freelancer-details-section__btns">
                <button
                    data-testid="editBtn"
                    type="button"
                    className="freelancer-details-section__btn"
                    onClick={handleEditButtonClick}
                >
                    <i className="icon icon-pencil-3"/>
                    Edit
                </button>
            </div>
        )
    );

    const renderTextFieldRow = (
        fieldName: keyof TFields,
        humanReadable: string,
        comment?: string,
    ) => (
        <tr>
            <td className="table-default__cell--text-blue">
                <span>{humanReadable}</span>
                {isEditing && comment && (
                    <>
                        <br/>
                        <span className="edit-profile-comment">{comment}</span>
                    </>
                )}
            </td>
            <td>
                {isEditing ? (
                    <input
                        type="text"
                        name={fieldName}
                        value={fields[fieldName]}
                        onChange={handleTextFieldChange}
                    />
                ) : (
                    clientData[fieldName]
                )}
            </td>
        </tr>
    );

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">Default assignment details</div>
                {renderSectionButtons()}
            </div>
            <div>
                {error &&
                    <div className="freelancer-details-section__errors">
                        <p>{error}</p>
                    </div>
                }
            </div>
            <div className="freelancer-details-section__content">
                <table className="table-default">
                    <tbody>
                    {renderTextFieldRow(
                        'default_nature_of_business',
                        `Nature of ${user.isClient ? 'your' : 'the Hirer’s'} business`,
                    )}
                    {renderTextFieldRow(
                        'default_required_qualifications',
                        'Experience and Training Required',
                    )}
                    {renderTextFieldRow(
                        'default_safety_risks',
                        'Health and safety risks',
                    )}
                    {renderTextFieldRow(
                        'default_available_facilities',
                        'Collective facilities',
                    )}
                    {renderTextFieldRow(
                        'default_expenses_payable',
                        'Any expenses payable by, or to the worker',
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
